import { config } from "@caixa/config";
import { normalizeModoAtendimento, type ModoAtendimento } from "@/lib/atendimentoMode";

export type ClientReceiptConfig = {
  legal_name: string;
  fantasy_name: string;
  cnpj: string;
  ie: string;
  im: string;
  address: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  uf: string;
  cep: string;
  phone: string;
  logo_url: string;
};

export type ClientConfig = {
  client_id: string;
  name: string;
  fantasy_name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  menu_logo_url: string;
  login_logo_url: string;
  nf_logo_url: string;
  background_logo_url: string;
  cnpj: string;
  ie: string;
  im: string;
  address: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  uf: string;
  cep: string;
  phone: string;
  base_url: string;
  modo_atendimento: ModoAtendimento;
  receipt: ClientReceiptConfig;
  /** Sem segredos: NFC-e apta no servidor (Focus + dados da empresa). */
  nfce_disponivel?: boolean;
  /** Sem segredos: entrada de estoque por IA habilitada no servidor. */
  ai_stock_enabled?: boolean;
};

const EMPTY_RECEIPT: ClientReceiptConfig = {
  legal_name: "GrandSystem",
  fantasy_name: "GrandSystem",
  cnpj: "",
  ie: "",
  im: "",
  address: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  uf: "",
  cep: "",
  phone: "",
  logo_url: "/logo-grand-system.svg",
};

export const GRANDSYSTEM_CLIENT_FALLBACK: ClientConfig = {
  client_id: "grandsystem",
  name: "GrandSystem",
  fantasy_name: "GrandSystem",
  primary_color: "#3B82F6",
  secondary_color: "#3B82F633",
  logo_url: "/logo-grand-system.svg",
  menu_logo_url: "/logo-grand-system.svg",
  login_logo_url: "/logo-grand-system.svg",
  nf_logo_url: "/logo-grand-system.svg",
  background_logo_url: "/logo-grand-system.svg",
  cnpj: "",
  ie: "",
  im: "",
  address: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  uf: "",
  cep: "",
  phone: "",
  base_url: "",
  modo_atendimento: "mesa",
  receipt: { ...EMPTY_RECEIPT },
};

function str(raw: Record<string, unknown>, key: string, fallback = ""): string {
  const v = raw[key];
  return typeof v === "string" ? v : fallback;
}

function normalizeReceipt(
  raw: Record<string, unknown>,
  receiptRaw: Record<string, unknown>,
  nfLogo: string,
): ClientReceiptConfig {
  return {
    legal_name:
      str(receiptRaw, "legal_name").trim() ||
      str(raw, "name").trim() ||
      GRANDSYSTEM_CLIENT_FALLBACK.receipt.legal_name,
    fantasy_name:
      str(receiptRaw, "fantasy_name").trim() ||
      str(raw, "fantasy_name").trim() ||
      GRANDSYSTEM_CLIENT_FALLBACK.receipt.fantasy_name,
    cnpj: str(receiptRaw, "cnpj", str(raw, "cnpj")),
    ie: str(receiptRaw, "ie", str(raw, "ie")),
    im: str(receiptRaw, "im", str(raw, "im")),
    address: str(receiptRaw, "address") || str(raw, "address"),
    number: str(receiptRaw, "number", str(raw, "number")),
    complement: str(receiptRaw, "complement", str(raw, "complement")),
    district: str(receiptRaw, "district", str(raw, "district")),
    city: str(receiptRaw, "city", str(raw, "city")),
    uf: str(receiptRaw, "uf", str(raw, "uf")),
    cep: str(receiptRaw, "cep", str(raw, "cep")),
    phone: str(receiptRaw, "phone", str(raw, "phone")),
    logo_url: str(receiptRaw, "logo_url").trim() || nfLogo,
  };
}

function normalizeClientConfig(raw: Record<string, unknown>): ClientConfig {
  const receiptRaw =
    raw.receipt && typeof raw.receipt === "object" ? (raw.receipt as Record<string, unknown>) : {};
  const menuLogo =
    str(raw, "menu_logo_url").trim() ||
    str(raw, "logo_url").trim() ||
    GRANDSYSTEM_CLIENT_FALLBACK.menu_logo_url;
  const loginLogo = str(raw, "login_logo_url").trim() || menuLogo;
  const nfLogo =
    str(raw, "nf_logo_url").trim() || str(raw, "logo_url").trim() || GRANDSYSTEM_CLIENT_FALLBACK.nf_logo_url;
  const bgLogo = str(raw, "background_logo_url").trim() || menuLogo;
  const receipt = normalizeReceipt(raw, receiptRaw, nfLogo);

  return {
    client_id: str(raw, "client_id") || GRANDSYSTEM_CLIENT_FALLBACK.client_id,
    name: str(raw, "name").trim() || GRANDSYSTEM_CLIENT_FALLBACK.name,
    fantasy_name:
      str(raw, "fantasy_name").trim() || str(raw, "name").trim() || GRANDSYSTEM_CLIENT_FALLBACK.fantasy_name,
    primary_color: str(raw, "primary_color").trim() || GRANDSYSTEM_CLIENT_FALLBACK.primary_color,
    secondary_color: str(raw, "secondary_color").trim() || GRANDSYSTEM_CLIENT_FALLBACK.secondary_color,
    logo_url: str(raw, "logo_url").trim() || GRANDSYSTEM_CLIENT_FALLBACK.logo_url,
    menu_logo_url: menuLogo,
    login_logo_url: loginLogo,
    nf_logo_url: nfLogo,
    background_logo_url: bgLogo,
    cnpj: receipt.cnpj,
    ie: receipt.ie,
    im: receipt.im,
    address: str(raw, "address") || receipt.address,
    number: receipt.number,
    complement: receipt.complement,
    district: receipt.district,
    city: receipt.city,
    uf: receipt.uf,
    cep: receipt.cep,
    phone: receipt.phone,
    base_url: str(raw, "base_url").trim(),
    modo_atendimento: normalizeModoAtendimento(raw.modo_atendimento),
    receipt,
    nfce_disponivel: raw.nfce_disponivel === true || raw.nfce_disponivel === 1,
    ai_stock_enabled: raw.ai_stock_enabled === true || raw.ai_stock_enabled === 1,
  };
}

export function applyClientTheme(config: ClientConfig): void {
  const root = document.documentElement;
  root.style.setProperty("--client-primary", config.primary_color);
  root.style.setProperty("--client-secondary", config.secondary_color);
}

export async function fetchClientConfig(): Promise<ClientConfig> {
  const base = config.BASE_URL.replace(/\/$/, "");
  try {
    const res = await fetch(`${base}/config/cliente.php`, {
      method: "GET",
      credentials: config.credentials,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return GRANDSYSTEM_CLIENT_FALLBACK;
    const data = (await res.json()) as Record<string, unknown>;
    if (data && data.ok === false) return GRANDSYSTEM_CLIENT_FALLBACK;
    const payload = data && typeof data === "object" ? { ...data } : {};
    delete payload.ok;
    return normalizeClientConfig(payload);
  } catch {
    return GRANDSYSTEM_CLIENT_FALLBACK;
  }
}

function buildReceiptAddress(receipt: ClientReceiptConfig): string {
  if (receipt.address.trim()) return receipt.address.trim();
  return [
    receipt.number ? `nº ${receipt.number}` : "",
    receipt.complement,
    receipt.district,
    receipt.city,
    receipt.uf,
    receipt.cep ? `CEP ${receipt.cep}` : "",
  ]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

export function receiptToEmpresaCupom(receipt: ClientReceiptConfig) {
  const endereco = buildReceiptAddress(receipt);
  return {
    nome: receipt.legal_name || receipt.fantasy_name,
    cnpj: receipt.cnpj,
    endereco,
    telefone: receipt.phone,
    ie: receipt.ie,
    logo_url: receipt.logo_url,
  };
}

let cachedClientConfig: ClientConfig | null = null;

export async function getClientConfig(): Promise<ClientConfig> {
  if (cachedClientConfig) return cachedClientConfig;
  cachedClientConfig = await fetchClientConfig();
  applyClientTheme(cachedClientConfig);
  return cachedClientConfig;
}

export function getCachedClientConfig(): ClientConfig {
  return cachedClientConfig ?? GRANDSYSTEM_CLIENT_FALLBACK;
}
